import { Tile } from "@dash/tile";
import { useTheme } from "@emotion/react";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { MdWarningAmber } from "react-icons/md";
import { Link } from "react-router-dom-v5-compat";

import { TicketListItemDto } from "@megaron/support-contracts";

import { toDisplayName } from "./displayName";
import { getSupportTicketListTextColor } from "./supportTicketListTextColor";
import { isSupportTicketAgedOpen } from "./isSupportTicketAgedOpen";
import { SupportTicketListStatusSelect } from "./SupportTicketListStatusSelect";
import { SupportTicketStripeTile } from "./SupportTicketStripeTile";

const priorityLabels: Record<string, string> = {
  low: "Niski",
  medium: "Średni",
  high: "Wysoki",
  urgent: "Pilny",
};

const priorityColor = (
  priority: TicketListItemDto["priority"],
  colors: { success: string; warning: string; danger: string; primary: string; secondaryText: string },
) => {
  if (priority === "low") return colors.success;
  if (priority === "medium") return colors.warning;
  if (priority === "high") return colors.danger;
  if (priority === "urgent") return colors.primary;
  return colors.secondaryText;
};

const categoryLabels: Record<string, string> = {
  billing: "Rozliczenia",
  technical: "Techniczne",
  sales: "Sprzedaż",
  complaint: "Reklamacja",
  other: "Inne",
};

type Props = {
  item: TicketListItemDto;
  basePath: string;
};

export const SupportTicketMobileCard = ({ item, basePath }: Props) => {
  const theme = useTheme();
  const isAgedOpen = isSupportTicketAgedOpen(item);
  const reporterName = toDisplayName(item.reporterName || item.reporterEmail.split("@")[0]);
  const updatedDate = item.lastReplyAt ?? item.createdAt;
  const updatedAgo = updatedDate
    ? formatDistanceToNow(new Date(updatedDate), { addSuffix: true, locale: pl })
    : null;

  return (
    <Tile
      stripe={
        <div css={{ position: "relative" }}>
          <SupportTicketStripeTile item={item} />
        </div>
      }
      css={{
        color: getSupportTicketListTextColor(theme, item.status),
      }}
    >
      <Link
        to={`${basePath}/${item.id}`}
        css={{
          width: "100%",
          ":hover": {
            textDecoration: "none",
            "& > div > div:first-of-type": {
              textDecoration: "underline",
            },
          },
        }}
      >
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: "0.375rem",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <div
            css={{
              fontSize: "0.875rem",
              fontWeight: item.status !== "open" ? 400 : item.isRead ? 400 : 700,
              display: "flex",
              alignItems: "center",
              gap: "0.375rem",
            }}
          >
            {isAgedOpen && <MdWarningAmber css={{ color: theme.colors.warning, flexShrink: 0 }} size={16} />}
            <span css={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.subject}</span>
          </div>
          <div css={{ fontSize: "0.75rem", color: theme.colors.secondaryText, textDecoration: "none" }}>
            {[reporterName, updatedAgo ? `Zaktualizowano ${updatedAgo}` : null].filter(Boolean).join(" · ")}
          </div>
          <div css={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem", flexWrap: "wrap" }}>
            {item.category && (
              <span
                css={{
                  fontSize: "0.75rem",
                  padding: "0.125rem 0.5rem",
                  borderRadius: "1rem",
                  background: theme.colors.primaryLight,
                  color: theme.colors.primary,
                }}
              >
                {categoryLabels[item.category] ?? item.category}
              </span>
            )}
            {item.priority && (
              <span
                css={{
                  fontSize: "0.75rem",
                  padding: "0.125rem 0.5rem",
                  borderRadius: "1rem",
                  background: theme.colors.hover,
                  color: priorityColor(item.priority, theme.colors),
                }}
              >
                {priorityLabels[item.priority] ?? item.priority}
              </span>
            )}
            {item.sourceMailbox && (
              <span css={{ fontSize: "0.75rem", color: theme.colors.secondaryText }}>{item.sourceMailbox}</span>
            )}
          </div>
        </div>
      </Link>
      <SupportTicketListStatusSelect item={item} />
    </Tile>
  );
};
